import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { C, PageHeader, GlassCard, Badge, Skeleton, StatusDot, Sparkline, Tabs, Stat, Button, ProgressBar } from "@coremonit/design-system";
import { Boxes, ArrowLeft, Sparkles, Bot } from "lucide-react";
import { useService, useServiceHealth, useServiceMetrics, useServicePods } from "../api/hooks";
import { DeploymentTimelinePanel } from "../components/DeploymentTimelinePanel";
import { RelatedResources } from "../components/RelatedResources";

const TABS = [
  { id: "overview",    label: "Overview" },
  { id: "metrics",     label: "Metrics" },
  { id: "traces",      label: "Traces" },
  { id: "logs",        label: "Logs" },
  { id: "deployments", label: "Deployments" },
  { id: "containers",  label: "Containers" },
  { id: "health",      label: "Health" },
  { id: "dependencies",label: "Dependencies" },
  { id: "security",    label: "Security" },
];

export function ServiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const s = useService(id);
  const [tab, setTab] = useState(() => searchParams.get("tab") ?? "overview");

  useEffect(() => {
    const t = searchParams.get("tab");
    if (t) setTab(t);
  }, [searchParams]);
  const metrics = useServiceMetrics(id);
  const health = useServiceHealth(id);
  const pods = useServicePods(id);

  if (s.isLoading || !s.data) {
    return <div style={{ display: "flex", flexDirection: "column", gap: 12 }}><Skeleton height={40} /><Skeleton height={80} /><Skeleton height={300} /></div>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <PageHeader
        icon={<Boxes size={16} />}
        title={s.data.name}
        subtitle={`${s.data.team} · ${s.data.language} · ${s.data.runtime} · v${s.data.version}`}
        badge={<Badge color={s.data.environment === "production" ? "blue" : "muted"}>{s.data.environment}</Badge>}
        actions={
          <>
            <Button variant="ghost" size="sm" icon={<ArrowLeft size={12} />} onClick={() => nav("/services")}>All services</Button>
            <Button variant="glow" size="sm" icon={<Bot size={12} />} onClick={() => nav(`/ai?service=${s.data.id}`)}>Ask AI</Button>
          </>
        }
      />

      {/* Hero */}
      <GlassCard style={{ padding: 16 }} accent={s.data.status === "critical" ? "rgba(239,68,68,0.35)" : s.data.status === "warning" ? "rgba(245,158,11,0.35)" : "rgba(16,185,129,0.35)"}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <StatusDot status={s.data.status} pulse={s.data.status !== "healthy"} size={16} />
          <div style={{ flex: 1, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8 }}>
            <Stat small label="P95 latency"     value={`${s.data.latencyP95Ms}ms`} color={C.blue} />
            <Stat small label="Error rate"      value={`${s.data.errorRatePct.toFixed(2)}%`} color={s.data.errorRatePct > 1 ? C.red : C.green} />
            <Stat small label="RPM"             value={s.data.requestsPerMin.toLocaleString()} color={C.cyan} />
            <Stat small label="Availability"    value={`${s.data.availabilityPct.toFixed(2)}%`} color={C.green} />
            <Stat small label="Pods"            value={`${s.data.replicas.available}/${s.data.replicas.desired}`} color={s.data.replicas.available === s.data.replicas.desired ? C.green : C.amber} />
            <Stat small label="Restarts 24h"    value={s.data.restarts24h} color={s.data.restarts24h > 3 ? C.red : C.text} />
          </div>
        </div>
      </GlassCard>

      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      {tab === "overview" && (
        <>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <GlassCard style={{ padding: 14 }}>
            <SectionTitle title="Metadata" />
            <Info label="Environment" value={s.data.environment} />
            <Info label="Cluster"     value={s.data.deployment.cluster ?? "-"} />
            <Info label="Namespace"   value={s.data.deployment.namespace ?? "-"} />
            <Info label="Region"      value={s.data.deployment.region ?? "-"} />
            <Info label="Owner"       value={s.data.owner} />
            <Info label="Instrumentation" value={s.data.instrumentation.join(", ")} />
            <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 4 }}>
              {s.data.tags.map((t) => <Badge key={t} color="muted">{t}</Badge>)}
            </div>
          </GlassCard>
          <GlassCard style={{ padding: 14 }}>
            <SectionTitle title="Latency (last hour)" />
            {metrics.data && <Sparkline data={metrics.data.latencyP95.points.map((p) => p.value)} color={C.blue} width={280} height={72} />}
          </GlassCard>
          <GlassCard style={{ padding: 14 }}>
            <SectionTitle title="AI Health Score" />
            {health.data && (
              <>
                <div style={{ color: health.data.aiScore > 80 ? C.green : health.data.aiScore > 60 ? C.amber : C.red, fontSize: 32, fontWeight: 700 }}>{health.data.aiScore}</div>
                <ProgressBar value={health.data.aiScore} color={health.data.aiScore > 80 ? C.green : health.data.aiScore > 60 ? C.amber : C.red} />
                <div style={{ color: C.textMuted, fontSize: 10, marginTop: 6 }}>Blended score across availability, latency, errors, saturation and dependencies.</div>
              </>
            )}
          </GlassCard>
        </div>
        <RelatedResources links={{
          serviceId: s.data.id,
          clusterId: s.data.deployment.cluster,
          namespace: s.data.deployment.namespace,
          projectId: s.data.projectId,
        }} />
        </>
      )}

      {tab === "metrics" && metrics.data && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 10 }}>
          {(["cpu", "memory", "disk", "network", "requestRate", "errorRate", "latencyP95", "latencyP99", "gcPct", "heapMb", "threads", "connections"] as const).map((k) => (
            <GlassCard key={k} style={{ padding: 12 }}>
              <div style={{ color: C.textMuted, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>{k}</div>
              <Sparkline data={metrics.data[k].points.map(p => p.value)} color={C.blue} width={220} height={44} />
            </GlassCard>
          ))}
        </div>
      )}

      {tab === "deployments" && (
        <DeploymentTimelinePanel serviceId={s.data.id} serviceName={s.data.name} />
      )}

      {tab === "containers" && pods.data && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
          {pods.data.pods.map((p) => (
            <GlassCard key={p.id} style={{ padding: 12 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: C.text, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</div>
                  <div style={{ color: C.textMuted, fontSize: 9 }}>{p.node}</div>
                </div>
                <Badge color={p.status === "running" ? "green" : p.status === "pending" ? "amber" : "red"}>{p.status}</Badge>
              </div>
            </GlassCard>
          ))}
        </div>
      )}

      {(tab === "traces" || tab === "logs" || tab === "health" || tab === "dependencies" || tab === "security") && (
        <GlassCard style={{ padding: 40, textAlign: "center", color: C.textMuted, fontSize: 12 }}>
          Open the dedicated page for a full view.
          <div style={{ marginTop: 12 }}>
            <Button variant="glow" size="sm" onClick={() => nav(`/${tab === "dependencies" ? "dependencies" : tab === "traces" ? "traces" : tab === "logs" ? "logs" : tab === "security" ? "security" : "health"}?service=${s.data.id}`)}>Open {tab}</Button>
          </div>
        </GlassCard>
      )}
    </div>
  );
}

function SectionTitle({ title }: { title: string }) {
  return <div style={{ color: C.textSub, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 10 }}>{title}</div>;
}
function Info({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: `1px solid ${C.border}`, fontSize: 11 }}>
      <span style={{ color: C.textMuted }}>{label}</span>
      <span style={{ color: C.text, fontFamily: label === "Instrumentation" ? "inherit" : "ui-monospace, monospace" }}>{value}</span>
    </div>
  );
}
